import React, { useState, useEffect, createContext, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import Header from './components/Header';
import XUsernameSetup from './components/Auth';
import UploadForm from './components/UploadForm';
import SearchFilter from './components/SearchFilter';
import VideoList from './components/VideoList';
import './App.css';

// Create a context for theme and navigation management
export const ThemeContext = createContext(null);
// Create a context for Firebase services and user data
export const FirebaseContext = createContext(null);

function App() {
    // State for theme (light/dark) - Set initial theme to 'dark'
    const [theme, setTheme] = useState('dark');
    // State for Firebase database instance
    const [db, setDb] = useState(null);
    // State for Firebase auth instance
    const [auth, setAuth] = useState(null);
    // State for the current user (Firebase User object)
    const [currentUser, setCurrentUser] = useState(null);
    // State to track if Firebase auth state has been initially checked
    const [isAuthReady, setIsAuthReady] = useState(false);
    // State for the user's X username
    const [userXUsername, setUserXUsername] = useState(null);
    // State for search query
    const [searchQuery, setSearchQuery] = useState('');
    // State for selected category
    const [selectedCategory, setSelectedCategory] = useState('All');
    // State for general application errors
    const [error, setError] = useState(null);
    // State to manage the current view: 'browse' (homepage) or 'upload'
    const [currentView, setCurrentView] = useState('browse'); // Default to browse view

    // Callback to set the user's X username from XUsernameSetup component
    const handleUsernameSet = useCallback((username) => {
        setUserXUsername(username);
    }, []);

    // Function to change the current view
    const navigateTo = useCallback((view) => {
        setCurrentView(view);
    }, []);

    // Effect to initialize Firebase and set up auth listener
    useEffect(() => {
        try {
            // Use environment variables for Firebase configuration
            const firebaseConfig = {
                apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
                authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
                projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
                storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
                messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
                appId: process.env.REACT_APP_FIREBASE_APP_ID,
                measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
            };

            // Check if API key is defined before initializing Firebase
            if (!firebaseConfig.apiKey) {
                console.error("Firebase API Key is not defined. Please check your .env file.");
                setError("Firebase configuration missing. Cannot initialize app.");
                setIsAuthReady(true);
                return;
            }

            // Initialize Firebase app
            const app = initializeApp(firebaseConfig);
            // Get Firestore and Auth instances
            const firestoreDb = getFirestore(app);
            const firebaseAuth = getAuth(app);

            setDb(firestoreDb);
            setAuth(firebaseAuth);

            // Sign in anonymously for community access
            signInAnonymously(firebaseAuth)
                .then(() => console.log("Signed in anonymously"))
                .catch(error => {
                    console.error("Error signing in anonymously:", error);
                    setError(`Authentication error: ${error.message}. Please check Firebase config and rules.`);
                });

            // Set up authentication state change listener
            const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
                setCurrentUser(user);
                setIsAuthReady(true); // Mark auth as ready after initial check
                if (user) {
                    console.log("User is signed in:", user.uid);
                } else {
                    console.log("No user is signed in.");
                }
            });

            // Cleanup subscription on unmount
            return () => unsubscribe();
        } catch (error) {
            console.error("Failed to initialize Firebase:", error);
            setError("Failed to initialize Firebase. Please check your console for details.");
        }
    }, []); // Empty dependency array ensures this runs only once on mount

    // Effect to apply theme to the body tag
    useEffect(() => {
        document.body.setAttribute('data-theme', theme);
    }, [theme]);

    // Function to toggle theme
    const toggleTheme = () => {
        setTheme((prevTheme) => (prevTheme === 'light' ? 'dark' : 'light'));
    };

    // Determine the userId for Firestore operations
    const userId = currentUser?.uid || (isAuthReady ? crypto.randomUUID() : null);

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme, navigateTo, currentView }}>
            <FirebaseContext.Provider value={{ db, auth, currentUser, userId, isAuthReady }}>
                <div className="App">
                    {/* Pass userXUsername to Header */}
                    <Header userXUsername={userXUsername} />
                    <main className="main-content">
                        {/* Show loading message while Firebase auth is initializing */}
                        {!isAuthReady && <p className="loading-message">Initializing authentication...</p>}

                        {/* Show general application error if any */}
                        {error && <p className="error-message">{error}</p>}

                        {/* Show XUsernameSetup if auth is ready but userXUsername is not set */}
                        {isAuthReady && !userXUsername && !error && (
                            <div className="auth-section">
                                <XUsernameSetup onUsernameSet={handleUsernameSet} />
                            </div>
                        )}

                        {/* Show content based on currentView once authenticated and X username is set */}
                        {isAuthReady && currentUser && userXUsername && !error && (
                            <>
                                {currentView === 'browse' && (
                                    <section className="browse-section rounded-lg shadow-md p-4 m-4">
                                        <h2 className="section-title">Browse Game Edits</h2>
                                        <SearchFilter
                                            searchQuery={searchQuery}
                                            setSearchQuery={setSearchQuery}
                                            selectedCategory={selectedCategory}
                                            setSelectedCategory={setSelectedCategory}
                                        />
                                        <VideoList
                                            searchQuery={searchQuery}
                                            selectedCategory={selectedCategory}
                                            userId={userId}
                                            currentUserXUsername={userXUsername}
                                        />
                                    </section>
                                )}

                                {currentView === 'upload' && (
                                    <section className="upload-section rounded-lg shadow-md p-4 m-4">
                                        <h2 className="section-title">Upload Your Game Edit</h2>
                                        <UploadForm userId={userId} uploaderXUsername={userXUsername} />
                                    </section>
                                )}
                            </>
                        )}
                    </main>
                </div>
            </FirebaseContext.Provider>
        </ThemeContext.Provider>
    );
}

export default App;
